/**
 * HistoryList — lightweight, newest-first history rows.
 *
 * Pagination, pull-to-refresh, empty state, and the loading/end footer stay
 * in the list so the screen only owns data and layout composition.
 */

import { useCallback, useMemo, useRef, type ReactElement } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';

import { Brand, Colors, Spacing } from '@/constants/theme';
import type { HistoryItem } from '@/types/history';
import { HistoryCard } from './HistoryCard';

interface HistoryListProps {
  items: HistoryItem[];
  header?: ReactElement | null;
  footer?: ReactElement | null;
  emptyComponent?: ReactElement | null;
  hasMore: boolean;
  loading: boolean;
  onEndReached?: () => void;
  onItemPress: (item: HistoryItem) => void;
  todayIso: string;
  onPrint?: (item: HistoryItem) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
}

type ListRow = { type: 'card'; item: HistoryItem };

const WEB_PULL_THRESHOLD = 56;

export function HistoryList({
  items,
  header,
  footer,
  emptyComponent,
  hasMore,
  loading,
  onEndReached,
  onItemPress,
  todayIso,
  onPrint,
  onRefresh,
  refreshing,
}: HistoryListProps) {
  const scrollOffsetRef = useRef(0);
  const pullStartYRef = useRef<number | null>(null);
  const pullTriggeredRef = useRef(false);

  const rows = useMemo<ListRow[]>(
    () => items.map((item) => ({ type: 'card', item })),
    [items],
  );

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollOffsetRef.current = event.nativeEvent.contentOffset.y;
  }, []);

  const onTouchStart = useCallback((event: GestureResponderEvent) => {
    if (
      Platform.OS !== 'web' ||
      !onRefresh ||
      refreshing ||
      scrollOffsetRef.current > 0
    ) {
      return;
    }
    pullStartYRef.current = event.nativeEvent.pageY;
    pullTriggeredRef.current = false;
  }, [onRefresh, refreshing]);

  const onTouchMove = useCallback((event: GestureResponderEvent) => {
    if (
      Platform.OS !== 'web' ||
      !onRefresh ||
      refreshing ||
      pullStartYRef.current === null ||
      pullTriggeredRef.current
    ) {
      return;
    }
    if (scrollOffsetRef.current > 0) {
      pullStartYRef.current = null;
      return;
    }
    const distance = event.nativeEvent.pageY - pullStartYRef.current;
    if (distance >= WEB_PULL_THRESHOLD) {
      pullTriggeredRef.current = true;
      onRefresh();
    }
  }, [onRefresh, refreshing]);

  const onTouchEnd = useCallback(() => {
    pullStartYRef.current = null;
    pullTriggeredRef.current = false;
  }, []);

  const renderItem = useCallback(
    ({ item: row }: { item: ListRow }) => (
      <HistoryCard
        item={row.item}
        todayIso={todayIso}
        onPress={onItemPress}
        onPrint={onPrint}
      />
    ),
    [todayIso, onItemPress, onPrint],
  );

  const keyExtractor = useCallback((row: ListRow) => `c-${row.item.id}`, []);

  const ItemSeparator = useCallback(() => <View style={styles.separator} />, []);

  const listHeader = Platform.OS === 'web' && refreshing ? (
    <>
      <View style={styles.webRefreshIndicator}>
        <ActivityIndicator size="small" color={Brand.primary} />
      </View>
      {header}
    </>
  ) : header;

  const ListFooter = useCallback(() => (
    <>
      {loading ? (
        <View style={styles.footer}>
          <ActivityIndicator size="small" color={Brand.primary} />
        </View>
      ) : !hasMore && items.length > 0 ? (
        <View style={styles.endFooter}>
          <Text style={styles.endText}>— Semua aktivitas sudah ditampilkan —</Text>
        </View>
      ) : null}
      {footer}
    </>
  ), [loading, hasMore, items.length, footer]);

  return (
    <FlatList
      style={styles.list}
      data={rows}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      ItemSeparatorComponent={ItemSeparator}
      ListFooterComponent={ListFooter}
      ListHeaderComponent={listHeader}
      ListEmptyComponent={emptyComponent}
      onEndReached={onEndReached}
      onEndReachedThreshold={0.5}
      onRefresh={onRefresh}
      refreshing={refreshing}
      onScroll={Platform.OS === 'web' ? onScroll : undefined}
      scrollEventThrottle={Platform.OS === 'web' ? 16 : undefined}
      onTouchStart={Platform.OS === 'web' ? onTouchStart : undefined}
      onTouchMove={Platform.OS === 'web' ? onTouchMove : undefined}
      onTouchEnd={Platform.OS === 'web' ? onTouchEnd : undefined}
      onTouchCancel={Platform.OS === 'web' ? onTouchEnd : undefined}
      contentContainerStyle={[styles.listContent, rows.length === 0 && styles.emptyContent]}
      showsVerticalScrollIndicator={false}
    />
  );
}

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  listContent: {
    paddingBottom: Spacing.four,
  },
  emptyContent: {
    flexGrow: 1,
  },
  separator: {
    backgroundColor: Colors.light.border,
    height: 1,
  },
  footer: {
    alignItems: 'center',
    paddingVertical: Spacing.four,
  },
  webRefreshIndicator: {
    alignItems: 'center',
    paddingVertical: Spacing.two,
  },
  endFooter: {
    alignItems: 'center',
    paddingVertical: Spacing.five,
  },
  endText: {
    color: Colors.light.textSecondary,
    fontSize: 12,
    fontStyle: 'italic',
  },
});
